import {useMemo} from "react";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {CartTimerIcon, HotIcon, MegaphoneIcon, PercentIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";

import {InquiryButtonTypeName} from "../../../constants/inquiry_button_type";
import {ApplicationModalButton, IApplicationModalButtonRenderProps} from "../../application/components/ApplicationModalButton";
import {ApplicationModalStep} from "../../application/types/ApplicationModalStep";
import {GTMEventApplicationSourceSection} from "../../application/types/lead/GTMEvenApplicationSourceSection";
import {ApplicationSource, ApplicationSourceSection} from "../../application/utils/form/application_form_source";
import {IGTMEventLeadForm} from "../../tracking/google_tag_manager/gtm_event_form_open";
import {getPromotionListingDefaultMessage} from "../helpers/promotion_listing_config";
import {getPromotionTypeName, PromotionType} from "../helpers/PromotionType";
import {useAmplitudePromotionViewportTracking} from "../hooks/use_amplitude_promotion_viewport_tracking";
import {getAmplitudeOfferPagePromotionTrackingContext} from "../tracking/get_amplitude_offer_page_promotion_tracking_context";
import {getAmplitudePromotionLeadFormTrackingContext} from "../tracking/get_amplitude_promotion_lead_form_event_tracking";
import {trackAmplitudeOfferSelectPromotion} from "../tracking/track_amplitude_offer_promotion_events";

export type PromotionVariant = "default" | "offerDetail";

interface IProps {
    promotion: {
        description: string | null;
        id: number;
        name: string;
        type: PromotionType;
    };
    vendorId: number;
    isAutoresponderEnabled: boolean;
    offerName: string;
    offerId?: number;
    propertyId?: number;
    isSaleOverLimit?: boolean;
    variant?: PromotionVariant;
}

export const Promotion = (props: IProps) => {
    const {promotion, vendorId, offerId, propertyId, variant = "default"} = props;

    const promotionStyle = usePromotionStyle(promotion.type);
    const applicationSource = propertyId ? ApplicationSource.PromotionProperty : ApplicationSource.Promotion;
    const gtmData: IGTMEventLeadForm = {
        clickText: InquiryButtonTypeName.PROMOTIONS,
        source: GTMEventApplicationSourceSection.PROMOTION,
        vendorName: vendorId.toString(),
        offerName: offerId ? offerId.toString() : ""
    };

    const trackingContext = useMemo(() => {
        if (!offerId) {
            return null;
        }

        return getAmplitudeOfferPagePromotionTrackingContext({
            promotion,
            offerId,
            propertyId,
            creativeSlot: "promotion_box"
        });
    }, [offerId, propertyId, promotion]);

    const {elementId} = useAmplitudePromotionViewportTracking({
        trackingContext,
        elementId: promotion.id.toString(),
        disabled: !offerId
    });

    const amplitudeLeadFormTrackingContext = useMemo(() => {
        if (!trackingContext) {
            return null;
        }

        return getAmplitudePromotionLeadFormTrackingContext({
            promotionTrackingContext: trackingContext,
            applicationSource,
            ctaText: InquiryButtonTypeName.PROMOTIONS
        });
    }, [applicationSource, trackingContext]);

    const handlePromotionInquiryClick = (openModal: IApplicationModalButtonRenderProps["openModal"]) => {
        if (trackingContext) {
            trackAmplitudeOfferSelectPromotion(trackingContext);
        }

        if (amplitudeLeadFormTrackingContext) {
            openModal({gtm: gtmData, amplitude: amplitudeLeadFormTrackingContext});
            return;
        }

        openModal(gtmData);
    };

    const leadButton = (
        <ApplicationModalButton
            flowVariant="promotion"
            initialStep={ApplicationModalStep.PROMOTION_FORM}
            source={applicationSource}
            sourceSection={ApplicationSourceSection.MODAL}
            topic={{
                message: getPromotionListingDefaultMessage(promotion.type, props.offerName)
            }}
            vendorId={vendorId}
            offerId={offerId || null}
            propertyId={propertyId || null}
            render={({openModal}) => (
                <Button
                    type="button"
                    onClick={() => handlePromotionInquiryClick(openModal)}
                    variant="outlined_secondary"
                    size={variant === "offerDetail" ? "small" : "x-small"}
                    className={variant === "offerDetail" ? offerDetailCtaStyle : promotionButtonStyle}
                >
                    {InquiryButtonTypeName.PROMOTIONS}
                </Button>
            )}
        />
    );

    if (variant === "offerDetail") {
        return (
            <li className={offerDetailItemStyle} key={promotion.id} data-id={elementId}>
                <div className={offerDetailIconStyle}>{promotionStyle.icon}</div>

                <div className={offerDetailContentStyle}>
                    <Text className={cx(offerDetailOverlineStyle, getPromotionOverlineColorStyle(promotion.type))} as="p" variant="mini_header">
                        {getPromotionTypeName(promotion.type)}
                    </Text>

                    <Text as="p" variant="headline_6" className={offerDetailTitleStyle}>
                        {promotion.name}
                    </Text>

                    {promotion.description && (
                        <Text as="p" variant="body_copy_2" className={offerDetailDescriptionStyle}>
                            {promotion.description}
                        </Text>
                    )}

                    {leadButton}
                </div>
            </li>
        );
    }

    return (
        <li className={promotionLiStyle} key={promotion.id} data-id={elementId}>
            <div className={promotionIconColumnStyle}>{promotionStyle.icon}</div>

            <div>
                <Text className={cx(promotionTypeNameStyle, getPromotionOverlineColorStyle(promotion.type))} as="p" variant="mini_header">
                    {getPromotionTypeName(promotion.type)}
                </Text>

                <Text as="p" variant="headline_6" className={promotionNameStyle}>
                    {promotion.name}
                </Text>

                <Text as="p" variant="body_copy_2" className={promotionDescriptionStyle}>
                    {promotion.description && promotion.description}
                </Text>

                {leadButton}
            </div>
        </li>
    );
};

const promotionLiStyle = css({
    display: "flex",
    "&:not(:last-of-type)": {
        mb: "5.8rem"
    },
    md: {
        "&:not(:last-of-type)": {
            mb: "5.6rem"
        }
    }
});

const promotionIconColumnStyle = css({
    flex: "0 0 75px",
    md: {
        flex: "0 0 95px"
    }
});

const promotionNameStyle = css({
    m: 0,
    mb: 2
});

export const promotionTypeNameStyle = css({
    display: "inline-flex",
    textTransform: "uppercase",
    m: 0,
    mb: 2,
    px: "0.1rem"
});

const promotionDescriptionStyle = css({
    mb: 2
});

const promotionButtonStyle = css({
    padding: "1rem 1.4rem"
});

//  offerDetail variant styles (Panda CSS)
const offerDetailItemStyle = css({
    display: "flex",
    gap: 2,
    "&:not(:last-of-type)": {
        mb: 4
    }
});

const offerDetailIconStyle = css({
    flex: "0 0 50px"
});

const offerDetailContentStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start"
});

const offerDetailOverlineStyle = css({
    display: "inline-flex",
    textTransform: "uppercase",
    m: 0,
    mb: 2,
    px: "0.1rem"
});

const offerDetailTitleStyle = css({
    m: 0,
    mb: 2
});

const offerDetailDescriptionStyle = css({
    mb: 2
});

const offerDetailCtaStyle = css({
    px: "1.4rem"
});

const offerDetailOverlineColorOpenDaysStyle = css({backgroundColor: "var(--colors-background-200)"});
const offerDetailOverlineColorDiscountStyle = css({backgroundColor: "var(--colors-info)"});
const offerDetailOverlineColorLastMinuteStyle = css({backgroundColor: "var(--colors-warning)"});
const offerDetailOverlineColorPresaleStyle = css({backgroundColor: "var(--colors-success)"});
const offerDetailOverlineColorExhibitionStyle = css({backgroundColor: "var(--colors-gray-200)"});
const offerDetailOverlineColorDefaultStyle = css({backgroundColor: "var(--colors-highlight)"});

const getPromotionOverlineColorStyle = (type: PromotionType): string => {
    switch (type) {
        case PromotionType.OPEN_DAYS:
            return offerDetailOverlineColorOpenDaysStyle;
        case PromotionType.DISCOUNT:
            return offerDetailOverlineColorDiscountStyle;
        case PromotionType.LAST_MINUTE:
            return offerDetailOverlineColorLastMinuteStyle;
        case PromotionType.PRESALE:
            return offerDetailOverlineColorPresaleStyle;
        case PromotionType.EXHIBITION:
            return offerDetailOverlineColorExhibitionStyle;
        default:
            return offerDetailOverlineColorDefaultStyle;
    }
};

//  usePromotionStyle - Style configuration hook
interface IGetPromotionStyleOutput {
    icon: React.JSX.Element | null;
    color: string;
}

export function usePromotionStyle(type: PromotionType): IGetPromotionStyleOutput {
    switch (type) {
        case PromotionType.OPEN_DAYS:
            return {
                icon: <MegaphoneIcon size="2.56" wrapperSize="5" wrapperColor="var(--colors-background-200)" />,
                color: "var(--colors-background-200)"
            };
        case PromotionType.DISCOUNT:
            return {
                icon: <PercentIcon size="2.56" wrapperSize="5" wrapperColor="var(--colors-info)" />,
                color: "var(--colors-info)"
            };
        case PromotionType.LAST_MINUTE:
            return {
                icon: <CartTimerIcon size="2.56" wrapperSize="5" wrapperColor="var(--colors-warning)" />,
                color: "var(--colors-warning)"
            };
        case PromotionType.PRESALE:
            return {
                icon: <MegaphoneIcon size="2.56" wrapperSize="5" wrapperColor="var(--colors-success)" />,
                color: "var(--colors-success)"
            };
        case PromotionType.EXHIBITION: //TODO: Not used anymore, remove implementation and usages in the app
            return {icon: null, color: "var(--colors-gray-200)"};
        default:
            //  OFFER
            return {
                icon: <HotIcon size="2.56" wrapperSize="5" wrapperColor="var(--colors-highlight)" />,
                color: "var(--colors-highlight)"
            };
    }
}
import {useMemo} from "react";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {CartTimerIcon, HotIcon, MegaphoneIcon, PercentIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";

import {InquiryButtonTypeName} from "../../../constants/inquiry_button_type";
import {ApplicationModalButton, IApplicationModalButtonRenderProps} from "../../application/components/ApplicationModalButton";
import {ApplicationModalStep} from "../../application/types/ApplicationModalStep";
import {GTMEventApplicationSourceSection} from "../../application/types/lead/GTMEvenApplicationSourceSection";
import {ApplicationSource, ApplicationSourceSection} from "../../application/utils/form/application_form_source";
import {IGTMEventLeadForm} from "../../tracking/google_tag_manager/gtm_event_form_open";
import {getPromotionListingDefaultMessage} from "../helpers/promotion_listing_config";
import {getPromotionTypeName, PromotionType} from "../helpers/PromotionType";
import {useAmplitudePromotionViewportTracking} from "../hooks/use_amplitude_promotion_viewport_tracking";
import {getAmplitudeOfferPagePromotionTrackingContext} from "../tracking/get_amplitude_offer_page_promotion_tracking_context";
import {getAmplitudePromotionLeadFormTrackingContext} from "../tracking/get_amplitude_promotion_lead_form_event_tracking";
import {trackAmplitudeOfferSelectPromotion} from "../tracking/track_amplitude_offer_promotion_events";

export type PromotionVariant = "default" | "offerDetail";

interface IProps {
    promotion: {
        description: string | null;
        id: number;
        name: string;
        type: PromotionType;
    };
    vendorId: number;
    isAutoresponderEnabled: boolean;
    offerName: string;
    offerId?: number;
    propertyId?: number;
    isSaleOverLimit?: boolean;
    variant?: PromotionVariant;
}

export const Promotion = (props: IProps) => {
    const {promotion, vendorId, offerId, propertyId, variant = "default"} = props;

    const promotionStyle = usePromotionStyle(promotion.type);
    const applicationSource = propertyId ? ApplicationSource.PromotionProperty : ApplicationSource.Promotion;
    const gtmData: IGTMEventLeadForm = {
        clickText: InquiryButtonTypeName.PROMOTIONS,
        source: GTMEventApplicationSourceSection.PROMOTION,
        vendorName: vendorId.toString(),
        offerName: offerId ? offerId.toString() : ""
    };

    const trackingContext = useMemo(() => {
        if (!offerId) {
            return null;
        }

        return getAmplitudeOfferPagePromotionTrackingContext({
            promotion,
            offerId,
            propertyId,
            creativeSlot: "promotion_box"
        });
    }, [offerId, propertyId, promotion]);

    const {elementId} = useAmplitudePromotionViewportTracking({
        trackingContext,
        elementId: promotion.id.toString(),
        disabled: !offerId
    });

    const amplitudeLeadFormTrackingContext = useMemo(() => {
        if (!trackingContext) {
            return null;
        }

        return getAmplitudePromotionLeadFormTrackingContext({
            promotionTrackingContext: trackingContext,
            applicationSource,
            ctaText: InquiryButtonTypeName.PROMOTIONS
        });
    }, [applicationSource, trackingContext]);

    const handlePromotionInquiryClick = (openModal: IApplicationModalButtonRenderProps["openModal"]) => {
        if (trackingContext) {
            trackAmplitudeOfferSelectPromotion(trackingContext);
        }

        if (amplitudeLeadFormTrackingContext) {
            openModal({gtm: gtmData, amplitude: amplitudeLeadFormTrackingContext});
            return;
        }

        openModal(gtmData);
    };

    const leadButton = (
        <ApplicationModalButton
            flowVariant="promotion"
            initialStep={ApplicationModalStep.PROMOTION_FORM}
            source={applicationSource}
            sourceSection={ApplicationSourceSection.MODAL}
            topic={{
                message: getPromotionListingDefaultMessage(promotion.type, props.offerName)
            }}
            vendorId={vendorId}
            offerId={offerId || null}
            propertyId={propertyId || null}
            render={({openModal}) => (
                <Button
                    type="button"
                    onClick={() => handlePromotionInquiryClick(openModal)}
                    variant="outlined_secondary"
                    size={variant === "offerDetail" ? "small" : "x-small"}
                    className={variant === "offerDetail" ? offerDetailCtaStyle : promotionButtonStyle}
                >
                    {InquiryButtonTypeName.PROMOTIONS}
                </Button>
            )}
        />
    );

    if (variant === "offerDetail") {
        return (
            <li className={offerDetailItemStyle} key={promotion.id} data-id={elementId}>
                <div className={offerDetailIconStyle}>{promotionStyle.icon}</div>

                <div className={offerDetailContentStyle}>
                    <Text className={cx(offerDetailOverlineStyle, getPromotionOverlineColorStyle(promotion.type))} as="p" variant="mini_header">
                        {getPromotionTypeName(promotion.type)}
                    </Text>

                    <Text as="p" variant="headline_6" className={offerDetailTitleStyle}>
                        {promotion.name}
                    </Text>

                    {promotion.description && (
                        <Text as="p" variant="body_copy_2" className={offerDetailDescriptionStyle}>
                            {promotion.description}
                        </Text>
                    )}

                    {leadButton}
                </div>
            </li>
        );
    }

    return (
        <li className={promotionLiStyle} key={promotion.id} data-id={elementId}>
            <div className={promotionIconColumnStyle}>{promotionStyle.icon}</div>

            <div>
                <Text className={cx(promotionTypeNameStyle, getPromotionOverlineColorStyle(promotion.type))} as="p" variant="mini_header">
                    {getPromotionTypeName(promotion.type)}
                </Text>

                <Text as="p" variant="headline_6" className={promotionNameStyle}>
                    {promotion.name}
                </Text>

                <Text as="p" variant="body_copy_2" className={promotionDescriptionStyle}>
                    {promotion.description && promotion.description}
                </Text>

                {leadButton}
            </div>
        </li>
    );
};

const promotionLiStyle = css({
    display: "flex",
    "&:not(:last-of-type)": {
        mb: "5.8rem"
    },
    md: {
        "&:not(:last-of-type)": {
            mb: "5.6rem"
        }
    }
});

const promotionIconColumnStyle = css({
    flex: "0 0 75px",
    md: {
        flex: "0 0 95px"
    }
});

const promotionNameStyle = css({
    m: 0,
    mb: 2
});

export const promotionTypeNameStyle = css({
    display: "inline-flex",
    textTransform: "uppercase",
    m: 0,
    mb: 2,
    px: "0.1rem"
});

const promotionDescriptionStyle = css({
    mb: 2
});

const promotionButtonStyle = css({
    padding: "1rem 1.4rem"
});

//  offerDetail variant styles (Panda CSS)
const offerDetailItemStyle = css({
    display: "flex",
    gap: 2,
    "&:not(:last-of-type)": {
        mb: 4
    }
});

const offerDetailIconStyle = css({
    flex: "0 0 50px"
});

const offerDetailContentStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start"
});

const offerDetailOverlineStyle = css({
    display: "inline-flex",
    textTransform: "uppercase",
    m: 0,
    mb: 2,
    px: "0.1rem"
});

const offerDetailTitleStyle = css({
    m: 0,
    mb: 2
});

const offerDetailDescriptionStyle = css({
    mb: 2
});

const offerDetailCtaStyle = css({
    px: "1.4rem"
});

const offerDetailOverlineColorOpenDaysStyle = css({backgroundColor: "var(--colors-background-200)"});
const offerDetailOverlineColorDiscountStyle = css({backgroundColor: "var(--colors-info)"});
const offerDetailOverlineColorLastMinuteStyle = css({backgroundColor: "var(--colors-warning)"});
const offerDetailOverlineColorPresaleStyle = css({backgroundColor: "var(--colors-success)"});
const offerDetailOverlineColorExhibitionStyle = css({backgroundColor: "var(--colors-gray-200)"});
const offerDetailOverlineColorDefaultStyle = css({backgroundColor: "var(--colors-highlight)"});

const getPromotionOverlineColorStyle = (type: PromotionType): string => {
    switch (type) {
        case PromotionType.OPEN_DAYS:
            return offerDetailOverlineColorOpenDaysStyle;
        case PromotionType.DISCOUNT:
            return offerDetailOverlineColorDiscountStyle;
        case PromotionType.LAST_MINUTE:
            return offerDetailOverlineColorLastMinuteStyle;
        case PromotionType.PRESALE:
            return offerDetailOverlineColorPresaleStyle;
        case PromotionType.EXHIBITION:
            return offerDetailOverlineColorExhibitionStyle;
        default:
            return offerDetailOverlineColorDefaultStyle;
    }
};

//  usePromotionStyle - Style configuration hook
interface IGetPromotionStyleOutput {
    icon: React.JSX.Element | null;
    color: string;
}

export function usePromotionStyle(type: PromotionType): IGetPromotionStyleOutput {
    switch (type) {
        case PromotionType.OPEN_DAYS:
            return {
                icon: <MegaphoneIcon size="2.56" wrapperSize="5" wrapperColor="var(--colors-background-200)" />,
                color: "var(--colors-background-200)"
            };
        case PromotionType.DISCOUNT:
            return {
                icon: <PercentIcon size="2.56" wrapperSize="5" wrapperColor="var(--colors-info)" />,
                color: "var(--colors-info)"
            };
        case PromotionType.LAST_MINUTE:
            return {
                icon: <CartTimerIcon size="2.56" wrapperSize="5" wrapperColor="var(--colors-warning)" />,
                color: "var(--colors-warning)"
            };
        case PromotionType.PRESALE:
            return {
                icon: <MegaphoneIcon size="2.56" wrapperSize="5" wrapperColor="var(--colors-success)" />,
                color: "var(--colors-success)"
            };
        case PromotionType.EXHIBITION: //TODO: Not used anymore, remove implementation and usages in the app
            return {icon: null, color: "var(--colors-gray-200)"};
        default:
            //  OFFER
            return {
                icon: <HotIcon size="2.56" wrapperSize="5" wrapperColor="var(--colors-highlight)" />,
                color: "var(--colors-highlight)"
            };
    }
}
